import { useCallback, useEffect, useState } from "react";
import { Send, Loader2, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const TIERS = ["free", "starter", "growth", "enterprise"];
const STATUSES = ["active", "trialing", "past_due", "canceled"];

export default function AdminBroadcast() {
  const [aud, setAud] = useState({ tiers: [], statuses: [], state: "", active_30d: false, has_business: false });
  const [msg, setMsg] = useState({ title: "", body: "", kind: "info" });
  const [count, setCount] = useState(null);
  const [history, setHistory] = useState([]);
  const [denied, setDenied] = useState(false);
  const [sending, setSending] = useState(false);

  const loadHistory = useCallback(() => api.get("/admin/notifications/history").then((r) => setHistory(r.data.broadcasts)).catch((e) => { if (e.response?.status === 403) setDenied(true); }), []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const preview = useCallback(() => { api.post("/admin/notifications/preview", aud).then((r) => setCount(r.data.count)).catch(() => {}); }, [aud]);
  useEffect(() => { const t = setTimeout(preview, 300); return () => clearTimeout(t); }, [preview]);

  const toggleArr = (key, val) => setAud((a) => ({ ...a, [key]: a[key].includes(val) ? a[key].filter((x) => x !== val) : [...a[key], val] }));

  const send = async () => {
    setSending(true);
    try { const r = await api.post("/admin/notifications/broadcast", { ...aud, ...msg }); toast.success(`Sent to ${r.data.recipients} users`); setMsg({ title: "", body: "", kind: "info" }); loadHistory(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (denied) return <p className="text-sm text-[#71717A]" data-testid="admin-broadcast-denied">You don't have the "notifications.send" permission.</p>;

  const Chip = ({ active, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`text-xs px-3 py-1.5 rounded-full border capitalize ${active ? "fv-gold-gradient text-[#0A0A0B] font-semibold border-transparent" : "border-white/10 text-[#A1A1AA]"}`}>{children}</button>
  );

  return (
    <div data-testid="admin-broadcast" className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-5">
        <h3 className="text-white font-medium">Compose announcement</h3>
        <div>
          <label className="text-xs text-[#71717A]">Audience — subscription tier</label>
          <div className="flex flex-wrap gap-2 mt-2">{TIERS.map((t) => <Chip key={t} active={aud.tiers.includes(t)} onClick={() => toggleArr("tiers", t)} testid={`bc-tier-${t}`}>{t}</Chip>)}</div>
        </div>
        <div>
          <label className="text-xs text-[#71717A]">Subscription status</label>
          <div className="flex flex-wrap gap-2 mt-2">{STATUSES.map((t) => <Chip key={t} active={aud.statuses.includes(t)} onClick={() => toggleArr("statuses", t)} testid={`bc-status-${t}`}>{t}</Chip>)}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#71717A]">State (optional)</label>
            <Input value={aud.state} onChange={(e) => setAud({ ...aud, state: e.target.value })} placeholder="e.g. CA" data-testid="bc-state" className="bg-[#0d0d0f] border-white/10 text-white mt-1" /></div>
        </div>
        <div className="flex items-center justify-between"><span className="text-sm text-[#D4D4D8]">Active in last 30 days</span><Switch checked={aud.active_30d} onCheckedChange={(v) => setAud({ ...aud, active_30d: v })} data-testid="bc-active-30d" /></div>
        <div className="flex items-center justify-between"><span className="text-sm text-[#D4D4D8]">Has a business profile</span><Switch checked={aud.has_business} onCheckedChange={(v) => setAud({ ...aud, has_business: v })} data-testid="bc-has-business" /></div>

        <div className="rounded-xl border border-[#D4AF37]/25 bg-[#151310] px-4 py-3 flex items-center gap-2" data-testid="bc-preview-count">
          <Users className="w-4 h-4 text-[#D4AF37]" /><span className="text-sm text-[#D4D4D8]">Reaches <b className="text-[#D4AF37]">{count ?? "…"}</b> users</span>
        </div>

        <div className="space-y-3 pt-2">
          <Input value={msg.title} onChange={(e) => setMsg({ ...msg, title: e.target.value })} placeholder="Announcement title" data-testid="bc-title" className="bg-[#0d0d0f] border-white/10 text-white" />
          <Textarea value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} placeholder="Message…" data-testid="bc-body" className="bg-[#0d0d0f] border-white/10 text-white min-h-[100px]" />
          <Button onClick={send} disabled={sending || !count || !msg.title.trim() || !msg.body.trim()} data-testid="bc-send" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send to {count ?? 0} users</>}</Button>
        </div>
      </div>

      <div>
        <h3 className="text-white font-medium mb-3">Recent broadcasts</h3>
        <div className="space-y-2" data-testid="bc-history">
          {history.length ? history.map((b) => (
            <div key={b.broadcast_id} className="rounded-xl border border-white/8 bg-[#121214] px-5 py-3">
              <div className="flex justify-between"><span className="text-white text-sm">{b.title}</span><span className="text-[#D4AF37] text-xs">{b.recipients} sent</span></div>
              <p className="text-xs text-[#71717A] line-clamp-1 mt-1">{b.body}</p>
              <p className="text-[10px] text-[#52525B] mt-1">{new Date(b.created_at).toLocaleString()}</p>
            </div>
          )) : <p className="text-sm text-[#71717A]">No broadcasts yet.</p>}
        </div>
      </div>
    </div>
  );
}
